import { Component, type ErrorInfo, type ReactNode } from 'react'
import { AlertTriangle, RefreshCw } from 'lucide-react'
import { Button } from '@shared/components/ui/button'

interface ChunkErrorBoundaryProps {
  children: ReactNode
}

interface ChunkErrorBoundaryState {
  hasChunkError: boolean
}

/**
 * Specialized Error Boundary that catches chunk/module loading failures
 * (e.g., when a lazy-loaded component fails to download due to network issues
 * or a deployment that invalidated the old chunk hashes).
 *
 * It wraps around <Suspense> boundaries to handle the case where the dynamic
 * import promise rejects.
 */
export class ChunkErrorBoundary extends Component<ChunkErrorBoundaryProps, ChunkErrorBoundaryState> {
  constructor(props: ChunkErrorBoundaryProps) {
    super(props)
    this.state = { hasChunkError: false }
  }

  static getDerivedStateFromError(error: Error): Partial<ChunkErrorBoundaryState> | null {
    // Detect chunk loading errors by checking the error message patterns
    // Vite/Webpack produce errors like "Failed to fetch dynamically imported module"
    // or "Loading chunk X failed"
    const isChunkError =
      error.message.includes('dynamically imported module') ||
      error.message.includes('Loading chunk') ||
      error.message.includes('Failed to fetch') ||
      error.name === 'ChunkLoadError'

    if (isChunkError) {
      return { hasChunkError: true }
    }

    // Re-throw non-chunk errors so the parent ErrorBoundary catches them
    throw error
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    if (import.meta.env.DEV) {
      console.error('[ChunkErrorBoundary] Chunk load failed:', error, errorInfo)
    }
  }

  handleReload = () => {
    // A full page reload is the correct fix for chunk errors,
    // because the old chunk URLs are no longer valid after a new deployment.
    window.location.reload()
  }

  render() {
    if (this.state.hasChunkError) {
      return (
        <div className="flex h-64 flex-col items-center justify-center gap-4 text-center">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-500/10">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
          </div>
          <div className="flex flex-col gap-1">
            <p className="text-sm font-medium text-foreground">
              A newer version is available
            </p>
            <p className="text-xs text-muted-foreground">
              This page failed to load. A reload will fix the issue.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={this.handleReload}>
            <RefreshCw className="mr-2 h-3 w-3" />
            Reload Page
          </Button>
        </div>
      )
    }

    return this.props.children
  }
}

export default ChunkErrorBoundary
